/** Centralized API endpoint constants routed through the API Gateway. */

const API = {
  AUTH: {
    LOGIN: '/api/identity/login',
    REFRESH: '/api/identity/refresh',
    LOGOUT: '/api/identity/logout',
    // WS-5.A — self-serve password reset (ADR-012). Anonymous; tenantId
    // travels in the body (the request interceptor mirrors it to
    // X-Tenant-Id since there's no authenticated user yet).
    FORGOT_PASSWORD: '/api/identity/forgot-password',
    RESET_PASSWORD: '/api/identity/reset-password',
    VALIDATE_ACTION_TOKEN: '/api/identity/validate-action-token',
    // Forced first-login password change for an account holding a temporary
    // password. Anonymous and PRE-session: the caller must send X-Tenant-Id
    // explicitly (the body carries no tenantId), exactly like admin-portal.
    CHANGE_PASSWORD: '/api/identity/change-password',
    // Marketing-site sign-in hand-off, step 2: exchange the single-use code for a session.
    // Anonymous and PRE-session: X-Tenant-Id is sent explicitly, like CHANGE_PASSWORD.
    HANDOFF_REDEEM: '/api/identity/handoff/redeem',
  },
  // Unified sessions endpoint served by SessionManagementService.
  // Returns every session in the caller's tenant, across ALL channels,
  // sorted by most-recent message first. Supersedes the old /conversations
  // feed that only covered consultations and didn't go through the gateway.
  SESSIONS: {
    LIST: '/api/sessions',
    DETAIL: (sessionId: string) => `/api/sessions/${sessionId}`,
    /**
     * Sets the conversation's staff-facing NAME. Mirrors the ADR-017 category endpoint's shape.
     * Body: `{ subject, origin? }`; an empty/blank subject CLEARS it, after which the console falls
     * back to `getConversationLabel`'s derived per-channel label. Renaming never changes the
     * session's state or its category.
     */
    SET_SUBJECT: (sessionId: string) => `/api/sessions/${sessionId}/subject`,
    /**
     * OP-1005 - "Spam / unwanted" on an EMAIL conversation (workspace.handle on the server).
     * POST body `{ blockSender }` -> 200 `{ sessionId, purgeAfterUtc, alreadyMarked }`; 400 `{ error }`
     * when it is not an email conversation; 404 `{ error }` for unknown / not this tenant's (one answer
     * for both). The conversation disappears for every agent of the tenant, the bot never answers it,
     * and it is deleted everywhere after the grace window (24 h) unless undone.
     */
    SPAM_MARK: (sessionId: string) => `/api/sessions/${encodeURIComponent(sessionId)}/spam`,
    /** OP-1005 - undo inside the grace window. 200 `{ sessionId }`; 409 `{ error }` when not marked; 404. */
    SPAM_UNDO: (sessionId: string) => `/api/sessions/${encodeURIComponent(sessionId)}/spam/undo`,
    /**
     * OP-1005 - the tenant's conversations currently marked as spam, newest first (the Undo view).
     * A literal route beside `/api/sessions/{id}`: the server's literal segment out-ranks the id route,
     * the same way `state-defs` does.
     */
    SPAM_LIST: '/api/sessions/spam',
    /**
     * ADR-017 D2 — sets the tenant's conversation category on one session. Body
     * `{ category, origin? }`; the category must be an ACTIVE state-def key. There is no "clear":
     * the request requires a value.
     *
     * Two server-side effects worth knowing at the call site — the write PINS the category, so
     * auto-categorisation stops touching this conversation (except the terminal `closed` trigger),
     * and it fans out over SignalR, which re-buckets the row into a different inbox directory.
     */
    SET_CATEGORY: (sessionId: string) => `/api/sessions/${sessionId}/category`,
    SET_PRIORITY: (sessionId: string) => `/api/sessions/${sessionId}/priority`,
    /**
     * ADR-017 D3 — per-tenant conversation-state definitions (labels / colours /
     * order) for the console directory section headers. Tenant claim-bound
     * server-side. A tenant with none answers [] (no auto-seeded defaults since PR-A, 2026-10-26).
     */
    STATE_DEFS: '/api/sessions/state-defs',
    /**
     * PR-A (2026-10-26) - the tenant's Agent Console settings as the server resolved them (`workspace.view`):
     * `{ tenantId, privateConversations, privateConversationsEffective, privateConversationsInherited, hideTagFolders,
     * updatedBy, updatedAt }`. READ ONLY here - the Admin Portal edits it. Refetched on `ReceiveConsoleConfigChanged`.
     */
    CONSOLE_SETTINGS: '/api/sessions/console-settings',
    /**
     * ADR-033 - per-folder conversation counts for the folder rail. GET with `groupBy=folder&onlyWithMessages=true`
     * -> `{ counts: { mine, my-consult, ..., 'cat:<key>': n } }`. A literal route beside `/api/sessions/{id}`: the
     * server's literal segment out-ranks the id route (like `state-defs` and `spam`). `groupBy=category` (the default)
     * is the older shape and is NOT what the rail wants.
     */
    COUNTS: '/api/sessions/counts',
    /**
     * ADR-033 - the tenant's conversation-TAG catalog, READ ONLY here (`workspace.view`). The console attaches and
     * detaches existing tags and never creates, renames or deletes one (D8: the catalog is Admin Portal only). A 404
     * (a server without the catalog yet) is an EMPTY catalog, not an error.
     */
    TAGS: '/api/sessions/tags',
    /**
     * ADR-033 - attach (POST) or detach (DELETE) ONE existing catalog tag on a session; both idempotent, both answer
     * `{ sessionId, tags, tagsVersion }`. `workspace.handle`, owner-or-shared on the server. Both path segments are
     * encoded: a tag key is server-minted ASCII but the console does not rely on that.
     */
    TAG: (sessionId: string, key: string) =>
      `/api/sessions/${encodeURIComponent(sessionId)}/tags/${encodeURIComponent(key)}`,
  },
  // Message history for a session. Routed through the gateway to
  // ConversationStoreService which owns the persistent transcript.
  // (The older RealTimeChatService /chat/messages endpoint was a stub that
  // always returned an empty list — left the Agent Console's conversation
  // panel blank even when messages clearly existed.)
  CHAT: {
    MESSAGES: (sessionId: string) => `/api/store/conversations/${sessionId}/messages`,
    /**
     * The STAFF-ONLY chat action timeline (who took the conversation over, who the bot consulted, who
     * closed it ...). Same gateway route and service as MESSAGES; policy `workspace.view` on the server,
     * tenant from the JWT claim. Query: `before` (the previous page's `nextBefore`) and `pageSize`.
     * Newest page first, entries oldest-first within a page. 404 = unknown OR not this tenant's (one
     * answer for both); an empty page is normal. Customers have no route to it.
     */
    TIMELINE: (sessionId: string) => `/api/store/conversations/${encodeURIComponent(sessionId)}/timeline`,
  },
  AGENTS: {
    /**
     * ADR-016 §D3 — resolved per-agent concurrency cap (HumanAgentHub).
     * GET returns the caller's OWN cap for the read-only load badge; PUT
     * (admin-only, `users.manage`) edits it. The console only reads its own.
     * (ME/LIST/AVAILABILITY were dead consts removed by OP-334c — not re-added.)
     */
    PROFILE: (agentId: string) => `/api/agent-profiles/${agentId}`,
    /**
     * The caller's own hub session row — carries the live `availability`.
     * Added 2026-08-02 so the console can restore the agent's chosen status
     * after a refresh instead of redrawing the store's 'OFFLINE' seed.
     */
    SESSIONS: (agentId: string) => `/api/agents/${agentId}/sessions`,
    /**
     * Team-presence roster (HumanAgentHubService AgentsController). Returns the
     * non-OFFLINE agent_sessions for the caller's tenant. Reachable through the
     * gateway once the `/api/agents/**` → strip-`/api` transform is in place
     * (the controller is `[Route("agents")]`). Feeds the Team view.
     */
    ONLINE: '/api/agents/online',
    /**
     * Tenant-scoped id→name lookup for staff users: `[{ agentId, displayName }]`.
     * IdentityAuthService `AgentDirectoryController`, gated `workspace.view`, reachable through the
     * gateway's existing `/api/identity/**` → `/api/auth/**` rewrite (no gateway change).
     *
     * NOT presence — that is {@link ONLINE}, which returns only non-OFFLINE agents from the hub.
     * This returns every ACTIVE staff user whether or not they are logged in, because a
     * conversation's owner is frequently someone who is currently offline.
     */
    DIRECTORY: '/api/identity/agent-directory',
  },
  /**
   * EscalationQueueService — reachable through the gateway once the
   * `/api/escalation/**` → strip-`/api` transform is in place (the controller
   * is `[Route("escalation")]`).
   *   GET  /api/escalation/queue        — active unassigned/assigned items
   *   GET  /api/escalation/status       — per-status counts
   *   GET  /api/escalation/my-load      — caller's own open-conversation count
   *   POST /api/escalation/{id}/assign  — claim an item ({ agentId })
   */
  ESCALATION: {
    QUEUE: '/api/escalation/queue',
    STATUS: '/api/escalation/status',
    MY_LOAD: '/api/escalation/my-load',
    ASSIGN: (escalationId: string) => `/api/escalation/${escalationId}/assign`,
  },
  /**
   * CrmIntegrationService customer context. `CustomersController` is `[Route("customers")]`; the
   * GATEWAY rewrites `/api/crm/customers/{**}` down to `/customers/{**}` (`crm-customers-route`,
   * Order -1). The controller is NOT re-prefixed — an earlier version of this comment said it was,
   * which would mislead anyone adding a sibling route.
   *   GET /api/crm/customers/{customerId}               — profile
   *   GET /api/crm/customers/{customerId}/interactions  — interaction history
   * Both are fail-soft on the client: an error/empty response shows a friendly
   * empty state, never blocks the conversation panel.
   */
  /**
   * AiAgentService, through the gateway's verbatim `/api/ai/**` route.
   *
   * ADR-019 — REHYDRATE for in-flight email reply drafts. Until this existed a draft
   * reached the console ONLY as a live SignalR push at creation time, so a draft created
   * while nobody was connected (or wiped by the next session-list refetch, which replaces
   * the row array) stayed invisible for its whole review window and then auto-sent
   * unreviewed. Fetched on boot and after every session-list load.
   */
  AI: {
    EMAIL_DRAFTS_PENDING: '/api/ai/email-drafts/pending',
  },
  CRM: {
    CUSTOMER: (customerId: string) => `/api/crm/customers/${encodeURIComponent(customerId)}`,
    CUSTOMER_INTERACTIONS: (customerId: string) =>
      `/api/crm/customers/${encodeURIComponent(customerId)}/interactions`,
    /**
     * The tenant's own CRM category on this conversation (CRM v2 WS-6.1). GET lists the
     * selectable values and the current selection; PUT sets them.
     *
     * Keyed on SESSION, not customer: a ticket exists per conversation and can exist for an
     * anonymous visitor with no customer record at all — so a customer-keyed route would have
     * nothing to address for exactly the conversations most likely to need categorising.
     */
    SESSION_CATEGORIES: (sessionId: string) =>
      `/api/crm/sessions/${encodeURIComponent(sessionId)}/crm-categories`,
    /**
     * The tenant's own CRM TICKET properties on this conversation, chosen in the CRM mapping screen.
     * GET returns each property with its portal label, type, options and CURRENT value; PUT sets one.
     *
     * Values come from a live read of the ticket, not from what we last wrote — which is the difference
     * between this and SESSION_CATEGORIES above, where a value changed in the portal stays invisible.
     */
    SESSION_PROPERTIES: (sessionId: string) =>
      `/api/crm/sessions/${encodeURIComponent(sessionId)}/properties`,
  },
  /**
   * Per-agent notification preferences — NotificationService. Route mapped through the
   * gateway's /api/notifications/{**} -> NotificationService rule (Step 1 of this task).
   * GET returns the caller's own preferences (defaults if none saved yet); PUT saves them.
   */
  NOTIFICATIONS: {
    PREFERENCES: (agentId: string) => `/api/notifications/${agentId}/preferences`,
  },
  /**
   * Learning-tag endpoints — SessionManagementService.
   * POST  /api/sessions/{sessionId}/learning-tag  — set/update tag
   * GET   /api/sessions/learning-tagged           — list non-default tagged sessions
   */
  LEARNING: {
    SET_TAG: (sessionId: string) => `/api/sessions/${sessionId}/learning-tag`,
    /**
     * Read the session's saved learning disposition. Same route as SET_TAG,
     * GET instead of POST. Added 2026-08-02: the control had no read path at
     * all, so it always rendered "Normal" after a refresh even though the
     * server had the agent's choice stored the whole time.
     */
    GET_TAG: (sessionId: string) => `/api/sessions/${sessionId}/learning-tag`,
    LIST_TAGGED: '/api/sessions/learning-tagged',
  },
  /**
   * Explicit session close — SessionManagementService.
   * POST /api/sessions/{sessionId}/close — publishes SessionClosed (CRM filing,
   * analytics, widget ended-state). Body: { closedBy, reason }.
   */
  SESSIONS_CLOSE: (sessionId: string) => `/api/sessions/${sessionId}/close`,
  /**
   * WS-E.4 — MediaAttachmentService. GET bytes for a single attachment
   * (image-thumbnail preview / non-image chip download in the message
   * bubbles). Not deployed yet on a parallel stream — `fetchAttachmentBlob`
   * (in `./media`, NOT here — see WS-E.R-B2) callers must degrade
   * gracefully on failure.
   */
  MEDIA: {
    CONTENT: (fileId: string) => `/api/media/${encodeURIComponent(fileId)}/content`,
    /**
     * 2026-09-05 - a KNOWLEDGE-plane attachment (a bank image or article file the bot attached) is a
     * content hash served by KnowledgeBaseService, not a media id. Staff read it through the
     * staff-content route (knowledge.manage OR the console's staff token; ETag-cached, not gated on
     * Knowledge.Images.ServeEnabled), so a reviewer can see what the bot attached even before the
     * customer-facing serve switch is on. Neither route resolves the other plane's ids.
     */
    KNOWLEDGE_CONTENT: (sha256: string) => `/api/knowledge/images/${encodeURIComponent(sha256)}/staff-content`,
    /**
     * Server-advertised upload policy — enabled flag, max size, allowed MIME types. The console
     * hardcodes none of these: the server is the authority, and the customer widget already
     * reads the identical endpoint, so the two surfaces cannot drift apart.
     */
    CONFIG: '/api/media/config',
    /**
     * Upload one file against a session. sessionId is a QUERY parameter, not a body field — the
     * backend signature is (IFormFile file, [FromQuery] string sessionId).
     */
    UPLOAD: (sessionId: string) => `/api/media/upload?sessionId=${encodeURIComponent(sessionId)}`,
  },
} as const;

export default API;
